import { useEffect, useRef, useState } from "react";

import { debounce } from "@/utils/general";

/**
 * Measures the visible mobile ribbon height and syncs `--ribbon-height` on `:root`.
 * Used so sticky/fixed chrome and any consumers stay aligned when ribbon copy wraps.
 */
const useRibbonHeight = () => {
  const [height, setHeight] = useState(32);
  const visibleRibbonRef = useRef<HTMLElement | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const updateHeight = () => {
    const ribbons = document.querySelectorAll(".ribbon-mobile");

    visibleRibbonRef.current = null;
    for (const ribbon of ribbons) {
      if (
        ribbon instanceof HTMLElement &&
        ribbon.offsetParent !== null // checks visibility in layout
      ) {
        visibleRibbonRef.current = ribbon;
        break;
      }
    }

    if (visibleRibbonRef.current) {
      const rect = visibleRibbonRef.current.getBoundingClientRect();
      const h = Math.floor(rect.height);
      if (h > 0) {
        setHeight(h);
        document.documentElement.style.setProperty("--ribbon-height", `${h}px`);
      }

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
  };

  useEffect(() => {
    const debouncedUpdateHeight = debounce(updateHeight, 100);

    updateHeight();

    intervalRef.current = setInterval(updateHeight, 100);

    window.addEventListener("resize", debouncedUpdateHeight);

    const clearPollTimeout = setTimeout(() => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      window.removeEventListener("resize", debouncedUpdateHeight);
    }, 5000);

    return () => {
      clearTimeout(clearPollTimeout);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      window.removeEventListener("resize", debouncedUpdateHeight);
    };
  }, []);

  return height;
};

export default useRibbonHeight;
